import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { z } from 'zod';
import { type AbilitySummary } from '@apm/contracts';
import { apiFetch } from '../../lib/http';
import { cn } from '../../lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { StorageStatusBanner } from '../../features/storage/components/storage-status-banner';
import { usePendingTaskTypeCount } from '../../features/projects/use-pending-task-types';
import {
  ADMIN_GROUPS,
  visibleSections,
  type AdminSection,
  adminSectionById,
} from '../../features/admin/sections';

/**
 * Console quản trị — APM-401, chia mục từ APM-ADMIN-a.
 *
 * ## Một mục một lúc, và mục chưa mở thì chưa tải
 *
 * Trang này từng dựng cả 16 mục cạnh nhau. Ngoài chuyện phải cuộn rất dài, mỗi
 * panel tự gọi API khi mount — nên mở `/admin` là bắn hơn mười lăm lượt gọi
 * cùng lúc (kể cả `sensitive_access_log` và báo cáo chi phí gộp) để người dùng
 * đọc đúng một mục. Nay route dựng **một** `render()`, đúng mục đang mở.
 *
 * ## Mục đang mở nằm ở URL, không nằm ở `useState`
 *
 * Ba thứ đi kèm miễn phí khi nó ở URL: nút Back của trình duyệt hoạt động đúng
 * như người dùng chờ đợi, link dán cho đồng nghiệp mở ra đúng chỗ, và F5 không
 * ném họ về danh sách. Một `useState` mất cả ba, và mất im lặng.
 *
 * ⚠️ API phía sau đòi `SUPER_ADMIN`. Route này hiện với người có `user:manage`
 * (sidebar lọc theo đó), nên `ORG_ADMIN` mở được trang nhưng mọi lời gọi trả
 * 403 — và đó là hành vi đúng: chỗ chặn thật nằm ở backend.
 *
 * ⚠️ **Key AI không còn ở đây** (APM-805): nó thuộc về từng người dùng, nên nó
 * nằm ở `/settings`. Những gì còn lại ở trang này đều là cấp tổ chức thật.
 */
export const AdminSearch = z.object({
  /**
   * `z.string()` là đúng dù router phân tích search param bằng `JSON.parse`:
   * id ở đây là `storage`, `google-login`… — không chuỗi nào trong số đó là
   * JSON hợp lệ, nên chúng tới schema nguyên dạng chuỗi.
   *
   * Không dùng `z.enum(...)`: id lạ (link cũ, gõ tay) phải rơi về danh sách mục
   * chứ không phải làm hỏng cả route, và `adminSectionById` đã trả `undefined`
   * cho đúng trường hợp ấy.
   */
  section: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/admin')({
  component: AdminPage,
  validateSearch: AdminSearch,
});

function AdminPage(): ReactNode {
  const { t } = useTranslation();
  const { section: sectionId } = Route.useSearch();
  /*
    Lọc theo quyền hiệu lực — APM-ORG-f.

    `/me/abilities` là truy vấn mà layout đã gọi sẵn, nên đây chỉ đọc lại từ
    cache: không thêm một lượt gọi mạng nào cho trang này.
  */
  const abilities = useQuery({
    queryKey: ['abilities'],
    queryFn: () => apiFetch<AbilitySummary>('/me/abilities'),
    staleTime: 5 * 60_000,
  });
  const sections = visibleSections(abilities.data?.global ?? []);
  const pendingTaskTypes = usePendingTaskTypeCount(
    (abilities.data?.global ?? []).includes('user:manage'),
  );
  const section = adminSectionById(sectionId);

  /*
    Mục nằm ngoài quyền của người này thì coi như không có: rơi về danh sách.

    ⚠️ Xét trên `sections` chứ không trên `section`. Tra bằng
    `adminSectionById` là tra trên **toàn bộ** danh mục, nên một `ORG_ADMIN` dán
    link `?section=storage` vẫn mở ra khung cấu hình S3 rồi mới nhận 403 ở từng
    lời gọi — và trang trông như hỏng chứ không như bị từ chối.
  */
  if (section && sections.includes(section)) return <AdminSectionPage section={section} />;

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold tracking-tight">{t('nav.admin')}</h1>

      {/* Banner đứng đầu danh sách: chưa cấu hình S3 là trạng thái bình thường
          của hệ thống vừa cài, nhưng người dùng cần biết vì sao nút tải lên bị
          khoá — và biết trước khi phải đoán xem nó nằm ở mục nào. */}
      <StorageStatusBanner />

      {ADMIN_GROUPS.map((group) => (
        <section key={group} className="flex flex-col gap-3">
          <h2 className="text-sm font-medium text-muted-foreground">
            {t(`adminNav.group.${group}`)}
          </h2>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {sections
              .filter((item) => item.group === group)
              .map((item) => (
                <SectionCard
                  key={item.id}
                  section={item}
                  badge={item.id === 'task-types' ? pendingTaskTypes : 0}
                />
              ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * Thẻ trong danh sách là một `Link`, không phải `div` có `onClick`.
 *
 * Một thẻ điều hướng phải mở được bằng Ctrl+click hay chuột giữa, và phải đọc
 * được bằng bàn phím — `Link` cho cả hai mà không phải tự dựng `tabIndex` với
 * `onKeyDown`. Một `div` bắt click trông y hệt cho tới lúc ai đó cần một trong
 * hai thứ đó.
 */
function SectionCard({
  section,
  badge = 0,
}: {
  section: AdminSection;
  /** Số việc chờ xử lý ở mục này — APM-TT-e (đề xuất loại việc chờ duyệt). */
  badge?: number;
}): ReactNode {
  const { t } = useTranslation();
  const Icon = section.icon;

  return (
    <Link
      to="/admin"
      search={{ section: section.id }}
      className="rounded-DEFAULT border border-border bg-card p-4 transition-colors hover:border-primary/50 hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
    >
      <span className="flex items-start gap-3">
        <Icon aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex items-center gap-2 text-sm font-medium">
            {t(section.titleKey)}
            {badge > 0 ? (
              <span
                className="rounded-full bg-rag-red px-1.5 text-[10px] font-medium leading-4 text-white"
                aria-label={t('taskTypeCatalog.pendingBadge', { count: badge })}
              >
                {badge}
              </span>
            ) : null}
          </span>
          <span className="text-xs text-muted-foreground">{t(section.descriptionKey)}</span>
        </span>
        <ChevronRight aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      </span>
    </Link>
  );
}

function AdminSectionPage({ section }: { section: AdminSection }): ReactNode {
  const { t } = useTranslation();

  return (
    <div className={cn('flex flex-col gap-4', !section.wide && 'max-w-5xl')}>
      <Link
        to="/admin"
        search={{ section: undefined }}
        className="inline-flex items-center gap-1.5 self-start text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft aria-hidden className="size-4" />
        {t('adminNav.back')}
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>{t(section.titleKey)}</CardTitle>
          <CardDescription>{t(section.descriptionKey)}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">{section.render()}</CardContent>
      </Card>
    </div>
  );
}
